import { isDeepEqual, isMostlyStrictEqual } from '@attain-apps/data-utils'
import * as ngrxStore from '@ngrx/store'

/**
 * Historically, there has been confusion on what exactly the type parameters
 * should be to {@link ngrxStore.Selector}. This type was created to
 * address that confusion. All selectors produced by entity collections and
 * their plugins should be of this type, where the type parameter is just the
 * outputted type from this selector.
 *
 * @see {@link https://ngrx.io/guide/store/selectors}
 * @example
 * class MyPlugin extends ... {
 *   ...
 *   public get myEntitiesSl(): store.Selector<readonly MyEntity[]> {
 *     return (this.#myEntitiesSl ??= Selector.create(...))
 *   }
 *   ...
 * }
 */
export type Selector<out T> = ngrxStore.Selector<object, T>
export namespace Selector {
  /**
   * __NOTE:__ this should generally not be used directly. Instead, use
   * {@link Selector.create.usingGlobalState}
   */
  export const FOR_GLOBAL_STATE: Fn<object, object> = (state: object) => state

  /**
   * Creates a {@link Selector} that always returns the same value
   *
   * This should not be used frequently, as it defeats the purpose of having a
   * selector. It is only useful during certain edge cases when dynamically
   * creating selectors
   *
   * @example
   * function getItem(index: number): Selector<string | undefined> {
   *   return index < 0
   *     ? Selector.always(undefined)
   *     : Selector.create(myItemsSelector, myItems => myItems[index])
   * }
   */
  export function always<TResult>(result: TResult): Selector<TResult> {
    return () => result
  }

  /**
   * Creates a {@link Selector}, similar to
   * {@link https://ngrx.io/api/store/createSelector ngrxStore.createSelector},
   * but with some optimizations
   *
   * If you need to reference the global state, use
   * {@link create.usingGlobalState}
   *
   * If you want to change behavior for
   * {@link https://ngrx.io/guide/store/selectors#resetting-memoized-selectors NgRx memoization},
   * use one of the other functions in the {@link create} namespace
   */
  export function create<TSelectors extends readonly Fn<any, any>[], TResult>(
    ...args: Factory.Args<TSelectors, TResult>
  ): Selector<TResult>
  export function create(...inputs: Factory.Args<any, unknown>) {
    // Deep checking equality can help us use cache more often and thus skip
    // unnecessary updates. However, deep checking is more expensive than simple
    // equality checks. So we want to do it as little as necessary. Deep
    // checking args allows us to skip the selector logic when args have not
    // changed, and deep checking results allows us to skip any downstream logic
    // from the selector (e.g. other selectors that use this selector as an arg,
    // effects that listen to this selector as an observable, components that
    // use this selector as a signal, etc.) when the result has not changed.
    // Usually, selector logic is less computationally expensive than downstream
    // logic (such as other selectors and effects), and args are usually larger
    // than the result, which means deep checking the args is usually more
    // expensive than deep checking the result. For both those reasons, deep
    // checking just the result is generally the most performant
    return create.deepCheckResult(...inputs)
  }
  export namespace create {
    /**
     * Adds the global state as the first argument to the projection function
     *
     * When using the global state, checking the args for memoization is
     * pointless because something in the state should have changed. So instead,
     * we check the result of this selector
     */
    export const usingGlobalState: Factory<[typeof FOR_GLOBAL_STATE]> = (
      ...inputs
    ) => deepCheckResult(...[FOR_GLOBAL_STATE, ...inputs])

    /**
     * Similar to {@link create}. For checking equality during memoization, uses
     * {@link isMostlyStrictEqual} for args and result
     *
     * @see {@link https://ngrx.io/guide/store/selectors#resetting-memoized-selectors NgRx memoization}
     */
    export const simpleCheckArgsAndResult: Factory =
      ngrxStore.createSelectorFactory(projectionFn =>
        ngrxStore.defaultMemoize(
          projectionFn,
          isMostlyStrictEqual,
          isMostlyStrictEqual,
        ),
      )
    /**
     * Similar to {@link create}. For checking equality during memoization, uses
     * {@link isDeepEqual} for args and {@link isMostlyStrictEqual} for
     * result
     *
     * @see {@link https://ngrx.io/guide/store/selectors#resetting-memoized-selectors NgRx memoization}
     */
    export const deepCheckArgs: Factory = ngrxStore.createSelectorFactory(
      projectionFn =>
        ngrxStore.defaultMemoize(
          projectionFn,
          isDeepEqual,
          isMostlyStrictEqual,
        ),
    )
    /**
     * Similar to {@link create}. For checking equality during memoization, uses
     * {@link isMostlyStrictEqual} for args and {@link isDeepEqual} for
     * result
     *
     * @see {@link https://ngrx.io/guide/store/selectors#resetting-memoized-selectors NgRx memoization}
     */
    export const deepCheckResult: Factory = ngrxStore.createSelectorFactory(
      projectionFn =>
        ngrxStore.defaultMemoize(
          projectionFn,
          isMostlyStrictEqual,
          isDeepEqual,
        ),
    )
    /**
     * Similar to {@link create}. For checking equality during memoization, uses
     * {@link isDeepEqual isDeepEqual} for args and result
     */
    export const deepCheckArgsAndResult: Factory =
      ngrxStore.createSelectorFactory(projectionFn =>
        ngrxStore.defaultMemoize(projectionFn, isDeepEqual, isDeepEqual),
      )
  }

  /**
   * Function that produces a {@link Selector}
   */
  // TODO: maybe infer the TState for the resulting Selector
  export type Factory<THiddenSelectors extends readonly Fn<any, any>[] = []> = <
    TSelectors extends readonly Fn<any, any>[],
    TResult,
  >(
    ...args: Factory.Args<TSelectors, TResult, THiddenSelectors>
  ) => Selector<TResult>
  export namespace Factory {
    /**
     * Arguments for a {@link Factory}
     *
     * Consists of a variable number of {@link Fn} and then a projector function
     */
    export type Args<
      TSelectors extends readonly Fn<any, any>[],
      TResult,
      THiddenSelectors extends readonly Fn<any, any>[] = [],
    > = readonly [
      ...TSelectors,
      projector: (
        ...args: readonly [
          // Projector arguments provided by extra selectors added under-the-hood
          ...ResultsOf<THiddenSelectors>,
          // Projector arguments provided by the selectors given
          ...ResultsOf<TSelectors>,
        ]
      ) => TResult,
    ]
  }

  /**
   * At its core, a {@link Selector} is just a function that looks like this
   */
  export type Fn<TState, TResult> = (state: TState) => TResult

  /**
   * Results of a list of {@link Fn}
   */
  export type ResultsOf<TSelectors extends readonly Fn<any, any>[]> = {
    [K in keyof TSelectors]: TSelectors[K] extends Fn<any, infer V> ? V : never
  }
}
